/**
 * Ed25519 Signature Verification for HTTP Message Signatures
 * Implements proper cryptographic verification for ChatGPT and other agents
 * 
 * Based on RFC 9421 (HTTP Message Signatures) and ChatGPT's implementation
 * Reference: https://help.openai.com/en/articles/9785974-chatgpt-user-allowlisting
 */

/**
 * Known public keys for AI agents
 */
const KNOWN_KEYS = {
  chatgpt: [
    {
      kid: 'otMqcjr17mGyruktGvJU8oojQTSMHlVm7uO-lrcqbdg',
      // ChatGPT's current Ed25519 public key (base64)
      publicKey: '7F_3jDlxaquwh291MiACkcS3Opq88NksyHiakzS-Y1g',
      validFrom: new Date('2025-01-01').getTime() / 1000,
      validUntil: new Date('2025-04-11').getTime() / 1000,
    },
  ],
};

/**
 * Parse the Signature-Input header according to RFC 9421
 */
function parseSignatureInput(signatureInput: string): {
  keyid: string;
  created?: number | undefined;
  expires?: number | undefined;
  signedHeaders: string[];
} | null {
  try {
    // Example: sig1=("@method" "@path" "@authority" "date");keyid="...";created=1234567890
    const match = signatureInput.match(/sig1=\((.*?)\);(.+)/);
    if (!match) return null;

    const [, headersList, params] = match;
    
    // Parse signed headers
    const signedHeaders = headersList
      ? headersList
          .split(' ')
          .map(h => h.replace(/"/g, '').trim())
          .filter(h => h.length > 0)
      : [];

    // Parse parameters
    const keyidMatch = params ? params.match(/keyid="([^"]+)"/) : null;
    const createdMatch = params ? params.match(/created=(\d+)/) : null;
    const expiresMatch = params ? params.match(/expires=(\d+)/) : null;

    if (!keyidMatch || !keyidMatch[1]) return null;

    return {
      keyid: keyidMatch[1],
      created: createdMatch && createdMatch[1] ? parseInt(createdMatch[1]) : undefined,
      expires: expiresMatch && expiresMatch[1] ? parseInt(expiresMatch[1]) : undefined,
      signedHeaders,
    };
  } catch (error) {
    console.error('[Signature] Failed to parse Signature-Input:', error);
    return null;
  }
}

/**
 * Build the signature base string according to RFC 9421
 * This is what gets signed
 */
function buildSignatureBase(
  method: string,
  path: string,
  headers: Record<string, string>,
  signedHeaders: string[]
): string {
  const components: string[] = [];
  
  for (const headerName of signedHeaders) {
    let value: string;
    
    switch (headerName) {
      case '@method':
        value = method.toUpperCase();
        break;
      case '@path':
        value = path;
        break;
      case '@authority':
        // Get from Host header or URL
        value = headers['host'] || headers['Host'] || '';
        break;
      default:
        // Regular headers (case-insensitive lookup)
        const key = Object.keys(headers).find(
          k => k.toLowerCase() === headerName.toLowerCase()
        );
        value = key ? headers[key] || '' : '';
        break;
    }
    
    // Format according to RFC 9421
    components.push(`"${headerName}": ${value}`);
  }
  
  return components.join('\n');
}

/**
 * Verify Ed25519 signature using Web Crypto API
 */
async function verifyEd25519Signature(
  publicKeyBase64: string,
  signatureBase64: string,
  message: string
): Promise<boolean> {
  try {
    // Decode base64 to Uint8Array
    const publicKeyBytes = Uint8Array.from(atob(publicKeyBase64), c => c.charCodeAt(0));
    const signatureBytes = Uint8Array.from(atob(signatureBase64), c => c.charCodeAt(0));
    const messageBytes = new TextEncoder().encode(message);
    
    // Check key and signature lengths
    if (publicKeyBytes.length !== 32) {
      console.error('[Signature] Invalid public key length:', publicKeyBytes.length);
      return false;
    }
    if (signatureBytes.length !== 64) {
      console.error('[Signature] Invalid signature length:', signatureBytes.length);
      return false;
    }
    
    // Import the public key
    const publicKey = await crypto.subtle.importKey(
      'raw',
      publicKeyBytes,
      {
        name: 'Ed25519',
        namedCurve: 'Ed25519',
      },
      false,
      ['verify']
    );
    
    // Verify the signature
    const isValid = await crypto.subtle.verify(
      'Ed25519',
      publicKey,
      signatureBytes,
      messageBytes
    );
    
    return isValid;
  } catch (error) {
    console.error('[Signature] Ed25519 verification failed:', error);
    
    // Fallback: Try with @noble/ed25519 if available (for environments without Ed25519 support)
    if (typeof window === 'undefined') {
      try {
        // In Node.js/Edge Runtime, we might need to use a polyfill
        // For now, we'll return false if Web Crypto doesn't support Ed25519
        console.warn('[Signature] Ed25519 not supported in this environment');
        return false;
      } catch {
        return false;
      }
    }
    
    return false;
  }
}

/**
 * Signature verification result
 */
export interface SignatureVerificationResult {
  isValid: boolean;
  agent?: string;
  keyid?: string;
  confidence: number;
  reason?: string;
  verificationMethod: 'signature' | 'none';
}

/**
 * Verify HTTP Message Signature for AI agents
 */
export async function verifyAgentSignature(
  method: string,
  path: string,
  headers: Record<string, string>
): Promise<SignatureVerificationResult> {
  // Check for signature headers
  const signature = headers['signature'] || headers['Signature'];
  const signatureInput = headers['signature-input'] || headers['Signature-Input'];
  const signatureAgent = headers['signature-agent'] || headers['Signature-Agent'];
  
  // No signature present
  if (!signature || !signatureInput) {
    return {
      isValid: false,
      confidence: 0,
      reason: 'No signature headers present',
      verificationMethod: 'none',
    };
  }
  
  // Parse Signature-Input header
  const parsed = parseSignatureInput(signatureInput);
  if (!parsed) {
    return {
      isValid: false,
      confidence: 0,
      reason: 'Invalid Signature-Input header',
      verificationMethod: 'none',
    };
  }
  
  // Check timestamp if present
  if (parsed.created) {
    const now = Math.floor(Date.now() / 1000);
    const age = now - parsed.created;
    
    // Reject signatures older than 5 minutes
    if (age > 300) {
      return {
        isValid: false,
        confidence: 0,
        reason: 'Signature expired (older than 5 minutes)',
        verificationMethod: 'none',
      };
    }
    
    // Reject signatures from the future (clock skew tolerance: 30 seconds)
    if (age < -30) {
      return {
        isValid: false,
        confidence: 0,
        reason: 'Signature timestamp is in the future',
        verificationMethod: 'none',
      };
    }
  }
  
  // Determine which agent based on signature-agent header
  let agent: string | undefined;
  let knownKeys: typeof KNOWN_KEYS.chatgpt | undefined;
  
  if (signatureAgent === '"https://chatgpt.com"' || signatureAgent?.includes('chatgpt.com')) {
    agent = 'ChatGPT';
    knownKeys = KNOWN_KEYS.chatgpt;
  }
  // Add other agents here as needed
  
  if (!agent || !knownKeys) {
    return {
      isValid: false,
      confidence: 0,
      reason: 'Unknown signature agent',
      verificationMethod: 'none',
    };
  }
  
  // Find the key by ID
  const key = knownKeys.find(k => k.kid === parsed.keyid);
  if (!key) {
    return {
      isValid: false,
      confidence: 0,
      reason: `Unknown key ID: ${parsed.keyid}`,
      verificationMethod: 'none',
    };
  }
  
  // Check key validity period
  const now = Math.floor(Date.now() / 1000);
  if (now < key.validFrom || now > key.validUntil) {
    return {
      isValid: false,
      confidence: 0,
      reason: 'Key is not valid at current time',
      verificationMethod: 'none',
    };
  }
  
  // Build the signature base string
  const signatureBase = buildSignatureBase(method, path, headers, parsed.signedHeaders);
  
  // Extract the actual signature value (remove "sig1=:" prefix and "::" suffix if present)
  let signatureValue = signature;
  if (signatureValue.startsWith('sig1=:')) {
    signatureValue = signatureValue.substring(6);
  }
  if (signatureValue.endsWith(':')) {
    signatureValue = signatureValue.slice(0, -1);
  }
  
  // Verify the signature
  const isValid = await verifyEd25519Signature(
    key.publicKey,
    signatureValue,
    signatureBase
  );
  
  if (isValid) {
    return {
      isValid: true,
      agent,
      keyid: parsed.keyid,
      confidence: 1.0, // 100% confidence for valid signature
      verificationMethod: 'signature',
    };
  } else {
    return {
      isValid: false,
      confidence: 0,
      reason: 'Signature verification failed',
      verificationMethod: 'none',
    };
  }
}

/**
 * Quick check if signature headers are present (for performance)
 */
export function hasSignatureHeaders(headers: Record<string, string>): boolean {
  return !!(
    (headers['signature'] || headers['Signature']) &&
    (headers['signature-input'] || headers['Signature-Input'])
  );
}

/**
 * Check if this is a ChatGPT signature based on headers
 */
export function isChatGPTSignature(headers: Record<string, string>): boolean {
  const signatureAgent = headers['signature-agent'] || headers['Signature-Agent'];
  return signatureAgent === '"https://chatgpt.com"' || (signatureAgent?.includes('chatgpt.com') || false);
}